import { useVehicleRepository } from './useVehicleRepository';
import { useEffect, useCallback, useState, useContext } from 'react';
import { Vehicle } from 'core/models/vehicle';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import React from 'react';
import { cancellable } from './utility/cancellable';

type APIContextKey = string;

function makeAPIContextKey(projectId: Project.Id, environmentId: Environment.Id): APIContextKey {
    return `${projectId}/${environmentId}`;
}

const VehiclesCacheContext = React.createContext(new Map<APIContextKey, Vehicle[]>());

export function useWithVehicles() {
    return useCallback((children: React.ReactNode) => {
        const cache = new Map<APIContextKey, Vehicle[]>();

        function _() {
            return VehiclesCacheContext.Provider({
                value: cache,
                children
            });
        }
        return _;
    }, []);
}

export function useVehicles(projectId: Project.Id | undefined, environmentId: Environment.Id | undefined, force = false) {
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);

    const vehiclesCache = useContext(VehiclesCacheContext);

    const repository = useVehicleRepository();

    useEffect(() => {
        if (!projectId || !environmentId) return;

        const key = makeAPIContextKey(projectId, environmentId);

        if (!force) {
            const vehicles = vehiclesCache.get(key);
            if (vehicles !== undefined) {
                setVehicles(vehicles);
                return;
            }
        }

        const controller = new AbortController();

        return cancellable(async isCanceled => {
            repository.getVehicles(projectId, environmentId, controller)
                .then(vehicles => {
                    vehiclesCache.set(key, vehicles);
                    if (!isCanceled()) {
                        setVehicles(vehicles);
                    }
                });
        }, () => {
            controller.abort();
        });
    }, [environmentId, force, projectId, repository, vehiclesCache]);

    return vehicles;
}
